/**
 * Web Vitals Collector using Google's official web-vitals library
 * Based on Rybbit's proven implementation with improved error handling
 */

import { onLCP, onCLS, onINP, onFCP, onTTFB, Metric } from "web-vitals";
import { WebVitals } from "./types";

export interface WebVitalsData {
  lcp: number | null;
  cls: number | null;
  inp: number | null;
  fcp: number | null;
  ttfb: number | null;
}

export class WebVitalsCollector {
  private data: WebVitalsData = {
    lcp: null,
    cls: null,
    inp: null,
    fcp: null,
    ttfb: null,
  };

  private sent = false;
  private timeout: NodeJS.Timeout | null = null;
  private onReadyCallback: ((data: WebVitalsData) => void) | null = null;

  constructor(onReady: (data: WebVitalsData) => void) {
    this.onReadyCallback = onReady;
  }

  initialize(): void {
    try {
      // Track Core Web Vitals
      onLCP(this.collectMetric.bind(this));
      onCLS(this.collectMetric.bind(this));
      onINP(this.collectMetric.bind(this));

      // Track additional metrics
      onFCP(this.collectMetric.bind(this));
      onTTFB(this.collectMetric.bind(this));

      // Set timeout to send metrics even if not all are collected
      this.timeout = setTimeout(() => {
        if (!this.sent) {
          this.sendData();
        }
      }, 20000);

      // Send on page unload
      window.addEventListener("beforeunload", () => {
        if (!this.sent) {
          this.sendData();
        }
      });
    } catch (e) {
      console.warn("Error initializing web vitals tracking:", e);
    }
  }

  private collectMetric(metric: Metric): void {
    if (this.sent) return;

    const metricName = metric.name.toLowerCase() as keyof WebVitalsData;
    this.data[metricName] = metric.value;

    // Check if all metrics are collected
    const allCollected = Object.values(this.data).every(
      (value) => value !== null
    );
    if (allCollected) {
      this.sendData();
    }
  }

  private sendData(): void {
    if (this.sent) return;
    this.sent = true;

    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }

    if (this.onReadyCallback) {
      this.onReadyCallback(this.data);
    }
  }

  getData(): WebVitalsData {
    return { ...this.data };
  }

  reset(): void {
    this.data = {
      lcp: null,
      cls: null,
      inp: null,
      fcp: null,
      ttfb: null,
    };
    this.sent = false;
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }
}

/**
 * Legacy function for backward compatibility
 * Now uses WebVitalsCollector internally
 */
export function collectWebVitals(): Promise<WebVitals> {
  return new Promise((resolve) => {
    const collector = new WebVitalsCollector((data) => {
      // Convert WebVitalsData to WebVitals format with all required fields
      const vitals: WebVitals = {
        lcp: data.lcp,
        cls: data.cls,
        inp: data.inp,
        fcp: data.fcp,
        ttfb: data.ttfb
      };
      
      resolve(vitals);
    });
    
    collector.initialize();
    
    // Fallback: resolve with null vitals after 10 seconds for faster response
    setTimeout(() => {
      resolve({
        lcp: null,
        cls: null,
        inp: null,
        fcp: null,
        ttfb: null
      });
    }, 10000);
  });
}
